import { useEffect, useMemo, useRef, useState } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { BarChart, LineChart, type BarSeriesOption, type LineSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { getMonthlyReport, type MonthlyReport } from '../api.js';
import { ErrorState, Segmented } from '../ui/index.js';
import { useLoad } from '../lifeos/useLoad.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { dollars, monthLabel } from './format.js';
import './reports.css';

use([BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

type ChartOption = ComposeOption<
  BarSeriesOption | LineSeriesOption | GridComponentOption | LegendComponentOption | TooltipComponentOption
>;

/**
 * Reports' month-by-month trend (the Monarch "Reports" chart): income against
 * spending with the net between them, over 6–24 months. Where the spending
 * went is Cash flow's job ("Where it went", with the drill), so the old
 * per-category stack is gone rather than drawn twice.
 */

type Range = '6' | '12' | '24';
const RANGES: readonly { value: Range; label: string }[] = [
  { value: '6', label: '6M' },
  { value: '12', label: '12M' },
  { value: '24', label: '24M' },
];

/** Soft hues (the Monarch look) — green stays reserved for money coming in. */
const PALETTE = {
  light: {
    income: '#3dbb8a', spent: '#ec7f8c', net: '#5b6ee1',
    text: '#1c1c1e', muted: 'rgba(60,60,67,0.6)', grid: 'rgba(60,60,67,0.12)', surface: '#ffffff',
  },
  dark: {
    income: '#34a77b', spent: '#d9707c', net: '#8c9cf0',
    text: '#f5f5f7', muted: 'rgba(235,235,245,0.6)', grid: 'rgba(84,84,88,0.45)', surface: '#2c2c2e',
  },
} as const;

function axisMoney(v: number): string {
  const d = Math.abs(v) / 100;
  const s = d >= 1000 ? `$${Math.round(d / 100) / 10}k` : `$${Math.round(d)}`;
  return v < 0 ? `−${s}` : s;
}

function baseOption(mode: 'light' | 'dark', months: string[]): ChartOption {
  const p = PALETTE[mode];
  return {
    grid: { left: 8, right: 8, top: 36, bottom: 4, containLabel: true },
    legend: { top: 0, left: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 10, textStyle: { color: p.muted, fontSize: 12 } },
    tooltip: {
      trigger: 'axis',
      backgroundColor: p.surface,
      borderWidth: 0,
      textStyle: { color: p.text, fontSize: 12 },
      valueFormatter: (v) => dollars(typeof v === 'number' ? v : Number(v)),
    },
    xAxis: {
      type: 'category',
      data: months.map(monthLabel),
      axisLine: { lineStyle: { color: p.grid } },
      axisTick: { show: false },
      axisLabel: { color: p.muted, fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: p.grid } },
      axisLabel: { color: p.muted, fontSize: 11, formatter: (v: number) => axisMoney(v) },
    },
  };
}

function flowOption(report: MonthlyReport, mode: 'light' | 'dark'): ChartOption {
  const p = PALETTE[mode];
  const net = report.months.map((_, i) => (report.incomeCents[i] ?? 0) - (report.spentCents[i] ?? 0));
  return {
    ...baseOption(mode, report.months),
    series: [
      { name: 'Income', type: 'bar', data: report.incomeCents, itemStyle: { color: p.income, borderRadius: [4, 4, 0, 0] }, barGap: '12%', barMaxWidth: 22 },
      { name: 'Spending', type: 'bar', data: report.spentCents, itemStyle: { color: p.spent, borderRadius: [4, 4, 0, 0] }, barMaxWidth: 22 },
      {
        name: 'Net',
        type: 'line',
        data: net,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: p.net, width: 2 },
        itemStyle: { color: p.net },
      },
    ],
  };
}

/** A fresh chart is needed when there is none, it was disposed, or React swapped the node. */
function needsInit(chart: ECharts | null, el: HTMLElement): boolean {
  return chart === null || chart.isDisposed() || chart.getDom() !== el;
}

/** One echarts canvas, re-themed on light/dark and resized with its box. */
function Chart({ option, height, label }: { option: ChartOption; height: number; label: string }): JSX.Element {
  const elRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (needsInit(chartRef.current, el)) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    chartRef.current?.setOption(option, true);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [option]);

  useEffect(
    () => () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    },
    [],
  );

  return <div ref={elRef} className="mr-chart" style={{ height }} role="img" aria-label={label} />;
}

export default function Reports(): JSX.Element {
  const mode = useThemeMode();
  const [range, setRange] = useState<Range>('12');

  // Depth 1 keeps the payload small; only the income/spending series are drawn.
  const report = useLoad(() => getMonthlyReport({ months: Number(range), depth: 1 }), [range]);
  const data = report.data;

  const flow = useMemo(() => (data ? flowOption(data, mode) : null), [data, mode]);

  const totals = useMemo(() => {
    if (!data) return null;
    const income = data.incomeCents.reduce((s, v) => s + v, 0);
    const spent = data.spentCents.reduce((s, v) => s + v, 0);
    return { income, spent, net: income - spent, rate: income > 0 ? (income - spent) / income : null };
  }, [data]);

  return (
    <div className="mr-page">
      <div className="mr-toolbar">
        <h2 className="mr-h2">Month by month</h2>
        <span className="mr-month__spacer" />
        <Segmented label="Range" value={range} options={RANGES} onChange={setRange} />
      </div>

      {report.error && !data ? (
        <ErrorState message={report.error} onRetry={() => void report.reload()} />
      ) : !data || !flow || !totals ? (
        <div className="mr-loading" aria-busy="true">
          Loading…
        </div>
      ) : (
        <section className="mr-card" aria-label="Income and spending">
          <div className="mr-figs">
            <div className="mr-fig">
              <span className="mr-fig__label">Income</span>
              <span className="mr-fig__value mr-good">{dollars(totals.income)}</span>
            </div>
            <div className="mr-fig">
              <span className="mr-fig__label">Spending</span>
              <span className="mr-fig__value">{dollars(totals.spent)}</span>
            </div>
            <div className="mr-fig">
              <span className="mr-fig__label">Net</span>
              <span className={`mr-fig__value${totals.net < 0 ? ' mr-bad' : ''}`}>{dollars(totals.net, { sign: true })}</span>
            </div>
            {totals.rate !== null && (
              <div className="mr-fig">
                <span className="mr-fig__label">Saved</span>
                <span className="mr-fig__value">{Math.round(totals.rate * 100)}%</span>
              </div>
            )}
          </div>
          <Chart option={flow} height={260} label={`Income ${dollars(totals.income)}, spending ${dollars(totals.spent)} over ${data.months.length} months`} />
        </section>
      )}
    </div>
  );
}
